import { FC, forwardRef } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FAIcon, IconStyle } from '../Icons/Icons';
import RawTextLink from '../TextLink/RawTextLink';
import { Theme } from '../types/enums';
import { TextLinkUnderlineStyle } from '../TextLink/TextLink';
import { filterIconTheme, filterTextLinkTheme } from '../../utils';
import { AlertMessagePropsTypes, AlertMessageTheme } from './types/propsType';
import { TextWeight } from './types/enums';
import styles from './AlertMessage.module.scss';

function getThemeIcon(type: Theme, icon?: IconName) {
  if (icon) {
    return icon;
  }

  const typeToIcon: { [key: string]: IconName } = {
    [Theme.SUCCESS]: 'circle-check',
    [Theme.WARNING]: 'triangle-exclamation',
    [Theme.ERROR]: 'square-exclamation',
    [Theme.ACTION]: 'bolt',
    [Theme.GRAY]: 'bolt',
  };

  return typeToIcon[type] || null;
}
const AlertMessage: FC<AlertMessagePropsTypes> = forwardRef<HTMLDivElement, AlertMessagePropsTypes>(
  (
    {
      children,
      content,
      iconColor,
      hasIcon = true,
      theme = Theme.SUCCESS,
      onChevronClick,
      className = '',
      textLink,
      textWeight = TextWeight.REGULAR,
      icon,
      iconStyle,
    }: AlertMessagePropsTypes,
    ref,
  ) => {
    const notificationIconCondition = getThemeIcon(theme, icon);

    const getIconStyle = () => {
      if (iconStyle) {
        return iconStyle;
      } else if (theme === Theme.ACTION || theme === Theme.GRAY) {
        return IconStyle.SOLID;
      }
      return IconStyle.REGULAR;
    };

    return (
      <section
        className={`${styles['alert-message']} ${styles[theme]} ${styles[textWeight]} ${className} `}
        data-testid="alert-message-test-id"
        ref={ref}
      >
        <div className={styles['alert-message-main']}>
          {hasIcon && notificationIconCondition && (
            <span
              className={`${iconColor ? `${styles['icon-container']} ${styles[iconColor]}` : ''} ${
                styles['icon-selector']
              }`}
            >
              <FAIcon
                iconName={icon ? icon : notificationIconCondition}
                iconStyle={getIconStyle()}
                theme={filterIconTheme(iconColor || theme)}
                className={styles['icon']}
                data-testid="alert-message-icon"
              />
            </span>
          )}
          <div className={styles['alert-message-body']} data-testid="alert-message-body-test-id">
            {content ? (
              <>
                {content.heading ? (
                  <span className={styles['alert-message-body-heading']} data-testid="alert-message-heading-test-id">
                    {content.heading}
                  </span>
                ) : (
                  <></>
                )}
                {content.detail ? (
                  <>
                    <br />
                    <span className={styles['alert-message-body-detail']}>{content.detail}</span>
                  </>
                ) : (
                  <></>
                )}
              </>
            ) : (
              children
            )}
          </div>
        </div>
        <span className={styles['alert-message-action']}>
          {textLink?.text ? (
            <RawTextLink
              href={textLink.href}
              text={textLink.text}
              theme={filterTextLinkTheme(theme)}
              onClick={textLink.onClick}
              underline={TextLinkUnderlineStyle.FIXED}
              className={styles['alert-message-text-link']}
              data-testid="alert-message-text-link-test-id"
            />
          ) : onChevronClick ? (
            <FAIcon
              iconName="chevron-right"
              onClick={onChevronClick}
              theme={filterIconTheme(theme)}
              className={styles['alert-message-icon']}
              data-testid="alert-message-action-test-icon"
            />
          ) : null}
        </span>
      </section>
    );
  },
);
AlertMessage.displayName = 'AlertMessage';
export { TextWeight, IconStyle };
export default AlertMessage;
export type { AlertMessageTheme, AlertMessagePropsTypes };
